import type { ReactNode } from 'react';
import React from 'react';
import type { TooltipProps as AntdTooltipProps } from 'antd';
import { Tooltip as AntdTooltip } from 'antd';
import { useIntl } from 'react-intl';
import type { TooltipPlacement } from 'antd/lib/tooltip';
import isString from 'lodash/isString';
import { ClassNames, css } from '@emotion/react';
import styled from '@emotion/styled';
import { typeStyle } from 'constants/typography';

export type TooltipProps = {
  title?: string | ReactNode | (() => ReactNode);
  titleId?: string;
  titleValues?: Record<string, any>;
  placement: TooltipPlacement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // left undefined by default so existing callers keep antd's hover-only behaviour
  trigger?: AntdTooltipProps['trigger'];
  mouseEnterDelay?: AntdTooltipProps['mouseEnterDelay'];
  children?: ReactNode;
};

/**
 * `classNames.root` lands on `.ant-tooltip`, and antd's `:where(...)` selectors carry zero
 * specificity - so of these, only `max-width` needs doubling, being the one antd declares there.
 * Colours, padding, radius and shadow are antd tokens (the Tooltip block in antd.constants); the type
 * role goes on the inner, where antd sets no font.
 *
 * The link colour moves here from antd-wrapper's <Global> block.
 */
const overlayCss = css`
  word-break: break-word;

  && {
    max-width: 320px;
  }

  .ant-tooltip-inner {
    ${typeStyle.bodyMd}
  }

  /* The tooltip inverts the page, so --text-tooltip-link is the other mode's lime: lime-400 is 13.3:1
     on light's Gray 800 and lime-700 4.85:1 on dark's Gray 100, where the page's own --text-tint-lime
     would be 3.1:1 and 1.1:1. The hover selector is written out to outrank link.tsx's doubled-class hover. */
  .ant-tooltip-inner a,
  .ant-tooltip-inner a:hover {
    color: var(--text-tooltip-link);
  }
`;

const Tooltip: React.FunctionComponent<TooltipProps> = ({
  title,
  titleId,
  children,
  placement,
  titleValues,
  open,
  onOpenChange,
  trigger,
  mouseEnterDelay
}) => {
  const intl = useIntl();

  const antdTitle = titleId ? intl.formatMessage({ id: titleId }, titleValues) : (title ?? '');

  const formattedTitle = antdTitle && isString(antdTitle) ? <StyledTooltip>{antdTitle}</StyledTooltip> : antdTitle;

  return (
    // antd wants a className string, so the class has to be materialised. ClassNames is emotion's way
    // to do that without @emotion/css, which the lint rule bans for needing a CSP `unsafe-inline`.
    <ClassNames>
      {({ css: toClassName }) => (
        <AntdTooltip
          classNames={{ root: toClassName(overlayCss) }}
          title={formattedTitle}
          placement={placement}
          open={open}
          onOpenChange={onOpenChange}
          mouseEnterDelay={mouseEnterDelay}
          trigger={trigger}>
          {children}
        </AntdTooltip>
      )}
    </ClassNames>
  );
};

export default Tooltip;

const StyledTooltip = styled.div`
  max-height: 360px;
  overflow-y: auto;
  white-space: pre-wrap;
`;
